import type Handsontable from 'handsontable';

/**
 * Hidden rows and columns on the review grid are driven through the plugins'
 * API, never through the `hiddenRows` / `hiddenColumns` settings.
 *
 * The React wrapper pushes every prop through `updateSettings` on every
 * render, and Handsontable re-initialises a plugin whenever its key is present
 * in that call. For the hiding plugins that means destroying their index map
 * before their cell-meta hook is removed, and a cell rendered in that window
 * throws (`Cannot read properties of null (reading 'length')` from
 * `HidingMap.getValueAtIndex`). So the grid never passes those two settings:
 * {@link enableGridHiding} turns the plugins on once, with nothing hidden, and
 * {@link applyGridHiding} sets what is hidden after each render.
 */
export const GRID_HIDING_SETTINGS = {
  // Copy skips hidden rows, so a copied range is what's on screen. The gutter
  // marks where hidden rows and columns sit.
  hiddenRows: { rows: [], indicators: true, copyPasteEnabled: false },
  hiddenColumns: { columns: [], indicators: true },
} satisfies Handsontable.GridSettings;

/** Enable both hiding plugins, once; a no-op after that. */
export function enableGridHiding(hot: Handsontable): void {
  if (!hot.getSettings().hiddenRows) hot.updateSettings(GRID_HIDING_SETTINGS);
}

/**
 * Make exactly these rows and columns the hidden ones. A data swap resets the
 * plugins' maps, so this runs after every data change as well as every change
 * to what should be hidden.
 */
export function applyGridHiding(
  hot: Handsontable,
  rows: readonly number[],
  columns: readonly number[],
): void {
  const rowsPlugin = hot.getPlugin('hiddenRows');
  const columnsPlugin = hot.getPlugin('hiddenColumns');
  const changed =
    !sameIndexes(rowsPlugin.getHiddenRows(), rows) ||
    !sameIndexes(columnsPlugin.getHiddenColumns(), columns);
  if (!changed) return;
  hot.batch(() => {
    rowsPlugin.showRows(rowsPlugin.getHiddenRows());
    rowsPlugin.hideRows([...rows]);
    columnsPlugin.showColumns(columnsPlugin.getHiddenColumns());
    columnsPlugin.hideColumns([...columns]);
  });
  hot.render();
}

function sameIndexes(current: number[], wanted: readonly number[]): boolean {
  if (current.length !== wanted.length) return false;
  const sorted = [...current].sort((a, b) => a - b);
  return sorted.every((index, i) => index === wanted[i]);
}
